import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import {
    type ApiResult,
    type EidCategories,
    type OrganizationBrandingResponse,
    type OrganizationKey,
    Organizations,
    type UserData,
    UserDataClient,
    UserDataKey,
} from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';

import { type RootState } from '../reducers';
import {
    ACTION_SET_ADDRESS_STRING,
    ACTION_SET_PARTNER_CONVERSION_ID,
    ACTION_SET_WALLET_ADDRESSES,
    ACTION_SET_WALLET_DATA,
    type PartnerIntegrationActionTypes,
    type WalletAddressData,
} from '../types/partner-integrations';

import { setError } from './error';

export function setWalletData(walletData: OrganizationBrandingResponse): PartnerIntegrationActionTypes {
    return {
        type: ACTION_SET_WALLET_DATA,
        walletData,
    };
}

export function setAddressString(walletAddresses: EidCategories[]): PartnerIntegrationActionTypes {
    return {
        type: ACTION_SET_ADDRESS_STRING,
        walletAddresses,
    };
}

export function setWalletAddresses(
    walletAddresses: EidCategories[],
    walletAddressData?: WalletAddressData[],
): PartnerIntegrationActionTypes {
    return {
        type: ACTION_SET_WALLET_ADDRESSES,
        walletAddresses,
        walletAddressData,
    };
}

export function getAddresses() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<UserData | null>> {
        return apiFactory()
            .call(UserDataClient.getUserData, {
                key: UserDataKey.PartnerAddresses,
            })
            .then((r) => {
                if (r.ok && r.value && r.value?.value) {
                    dispatch(setAddressString(r.value?.value as EidCategories[]));
                }
                return r;
            })
            .catch((e) => {
                return e;
            });
    };
}

export function setUserPartnerAddresses(walletAddresses: EidCategories[]) {
    return async function () {
        await apiFactory().call(UserDataClient.putUserData, {
            key: UserDataKey.PartnerAddresses,
            value: { value: walletAddresses },
        });
    };
}

export function setPartnerConversionId(partnerConversionId: string): PartnerIntegrationActionTypes {
    return {
        type: ACTION_SET_PARTNER_CONVERSION_ID,
        partnerConversionId,
    };
}

export function getOrganizationsBranding(key: OrganizationKey) {
    return function (
        dispatch: ThunkDispatch<RootState, void, Action>,
    ): Promise<ApiResult<OrganizationBrandingResponse> | void> {
        return apiFactory()
            .call(Organizations.getBranding, { key })
            .then((r) => {
                if (r.ok && r.value) {
                    dispatch(setWalletData(r.value));
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, source: 'Organization Branding' }));
            });
    };
}
